import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Typography,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import { SheetData, JobStatus } from "../../pages/playground/types";

interface ClientFiltersBoxProps {
  sheetData: SheetData;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedStatus: string;
  setSelectedStatus: (status: string) => void;
  clientNotesFilter: string;
  setClientNotesFilter: (filter: string) => void;
  groupByColumn: string;
  setGroupByColumn: (column: string) => void;
}

export default function ClientFiltersBox({
  sheetData,
  searchQuery,
  setSearchQuery,
  selectedStatus,
  setSelectedStatus,
  clientNotesFilter,
  setClientNotesFilter,
  groupByColumn,
  setGroupByColumn,
}: ClientFiltersBoxProps) {
  // Constants for dropdown default values
  const STATUS_ALL = "ALL";
  const GROUP_NONE = "NONE";
  const NOTES_ALL = "ALL";
  const NOTES_WITH = "WITH_NOTES";
  const NOTES_WITHOUT = "WITHOUT_NOTES";

  // Status options for the dropdown
  const statusOptions = [
    { value: STATUS_ALL, label: "All Statuses" },
    { value: JobStatus.DELIVERED, label: "To be approved" },
    { value: JobStatus.TO_BE_REWORKED, label: "To be reworked" },
    { value: JobStatus.COMPLETED, label: "Approved" },
  ];

  // Client notes filter options
  const clientNotesOptions = [
    { value: NOTES_ALL, label: "All SKUs" },
    { value: NOTES_WITH, label: "With Client Notes" },
    { value: NOTES_WITHOUT, label: "Without Client Notes" },
  ];

  // Get sample values for columns when available
  const getColumnSample = (column: string) => {
    if (!sheetData.items || sheetData.items.length === 0) return null;

    // Try to get a sample value for this column
    const sampleValue = sheetData.items[0][column];
    if (sampleValue === undefined || sampleValue === null) return null;

    return (
      String(sampleValue).substring(0, 30) +
      (String(sampleValue).length > 30 ? "..." : "")
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "center",
        mb: 3,
        flexWrap: { xs: "wrap", md: "nowrap" },
      }}
    >
      {/* SKU Search */}
      <TextField
        size="small"
        placeholder="Search by SKU or Title"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ flex: 1 }}
      />

      {/* Status Filter */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="status-filter-label">Status</InputLabel>
        <Select
          labelId="status-filter-label"
          label="Status"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          {statusOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                fontWeight: option.value === STATUS_ALL ? 600 : 400,
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Client Notes Filter */}
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="client-notes-filter-label">Client Notes</InputLabel>
        <Select
          labelId="client-notes-filter-label"
          label="Client Notes"
          value={clientNotesFilter}
          onChange={(e) => setClientNotesFilter(e.target.value)}
        >
          {clientNotesOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                fontWeight: option.value === NOTES_ALL ? 600 : 400,
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Column Filter - Now Group By */}
      <FormControl
        size="small"
        sx={{ minWidth: 220, flex: 1, maxWidth: { md: 280 } }}
      >
        <InputLabel id="column-filter-label">Group by column</InputLabel>
        <Select
          labelId="column-filter-label"
          label="Group by column"
          value={groupByColumn}
          onChange={(e) => {
            setGroupByColumn(e.target.value);
          }}
          MenuProps={{
            PaperProps: {
              style: { maxHeight: 300 },
            },
          }}
        >
          <MenuItem value={GROUP_NONE} sx={{ fontWeight: 600 }}>
            No Grouping
          </MenuItem>
          {sheetData.columns &&
            sheetData.columns.map((column, index) => {
              const sample = getColumnSample(column);

              return (
                <MenuItem key={index} value={column}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography>{column}</Typography>
                    {sample && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        e.g. {sample}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>
    </Box>
  );
}
